import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  FooterRailRegistryContext,
  FooterRailStateContext,
  type FooterMoreRowDescriptor,
  type FooterRailRegistry,
  type FooterRailState,
} from './footer-rail-context';
import {
  readMembershipCache,
  writeMembershipCache,
} from './footer-rail-membership-cache';
import {
  FOOTER_RAIL_DISPLAY_ORDER,
  FOOTER_RAIL_ORDER,
  type FooterRailId,
} from './footer-rail.constants';
import { useFooterOverflow } from './use-footer-overflow';

interface FooterRailProviderProps {
  /** When false, every item stays in place (non-ribbon modes). */
  enabled: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Owns the single overflow decision and hands it to both host files through
 * context. Collapsible items register a descriptor; the decision reads only the
 * viewport-keyed budget, never the collapsed footer's own height.
 */
export function FooterRailProvider({
  enabled,
  className,
  children,
}: FooterRailProviderProps) {
  // Membership (id → pinned) drives the overflow decision; descriptors are held
  // separately so a nav-driven descriptor change refreshes only the More rows and
  // never churns `orderedIds`/`pinnedIds` (which would re-measure and flicker).
  // Seed from the last mount so a post-navigation remount's first split is already
  // collapsed instead of flashing the items into the rail (see the cache module).
  const [membership, setMembership] = useState<ReadonlyMap<FooterRailId, boolean>>(() =>
    readMembershipCache()
  );
  // Only the enabled provider writes, so a disabled (mobile) instance can't clobber
  // the desktop decision the next mount seeds from.
  useEffect(() => {
    if (enabled) writeMembershipCache(membership);
  }, [enabled, membership]);
  const [descriptors, setDescriptors] = useState<
    ReadonlyMap<FooterRailId, FooterMoreRowDescriptor>
  >(new Map());
  const [moreLabel, setMoreLabelState] = useState('More');

  const registry = useRef<FooterRailRegistry>({
    setMoreLabel: (label) => setMoreLabelState((prev) => (prev === label ? prev : label)),
    register: (id, pinned) =>
      setMembership((prev) => {
        if (prev.get(id) === pinned) return prev;
        const next = new Map(prev);
        next.set(id, pinned);
        return next;
      }),
    unregister: (id) =>
      setMembership((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Map(prev);
        next.delete(id);
        return next;
      }),
    setDescriptor: (id, descriptor) =>
      setDescriptors((prev) => {
        if (prev.get(id) === descriptor) return prev;
        const next = new Map(prev);
        next.set(id, descriptor);
        return next;
      }),
    removeDescriptor: (id) =>
      setDescriptors((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Map(prev);
        next.delete(id);
        return next;
      }),
  }).current;

  const orderedIds = useMemo(
    () => FOOTER_RAIL_ORDER.filter((id) => membership.has(id)),
    [membership]
  );
  const pinnedIds = useMemo(
    () => orderedIds.filter((id) => membership.get(id)),
    [orderedIds, membership]
  );

  const { moreIds, footerRef } = useFooterOverflow({
    enabled,
    orderedIds,
    pinnedIds,
  });

  // Rows render in footer-visual order, not collapse-priority order.
  const moreDescriptors = useMemo<readonly FooterMoreRowDescriptor[]>(() => {
    const inMore = new Set(moreIds);
    return FOOTER_RAIL_DISPLAY_ORDER.filter((id) => inMore.has(id))
      .map((id) => descriptors.get(id))
      .filter((d): d is FooterMoreRowDescriptor => d !== undefined);
  }, [moreIds, descriptors]);

  const state = useMemo<FooterRailState>(() => {
    const moreSet = new Set(moreIds);
    return {
      moreIds,
      moreDescriptors,
      moreLabel,
      isCollapsed: (id) => moreSet.has(id),
    };
  }, [moreIds, moreDescriptors, moreLabel]);

  return (
    <FooterRailRegistryContext.Provider value={registry}>
      <FooterRailStateContext.Provider value={state}>
        <div ref={footerRef} className={className}>
          {children}
        </div>
      </FooterRailStateContext.Provider>
    </FooterRailRegistryContext.Provider>
  );
}
